// ponytail: solid-CTA override. Only partners whose brand has a second hue need
// this; omit it and Chakra's default solid picks up the partner's `blue` scale.
type PartnerCta = {
  bg: string;
  fg: string;
  hover: string;
  active: string;
};

export type Partner = {
  name: string;
  logo: string;
  /** --brand-* ramp pushed onto :root while this partner is active. */
  vars: Record<string, string>;
  /** Replaces Chakra's `blue` scale — see theme/theme.ts. */
  blue: Record<number, string>;
  cta?: PartnerCta;
  /** Partner's own support inbox; when set, Help & Support shows it instead of hiding. */
  supportEmail?: string;
};

// ponytail: partner whitelabel registry, keyed by agent_id. Every colour the app
// can flip lives in the entry, so nothing downstream needs to know which partner
// is live — add a partner here and the whole app follows.
// NOTE: keep real hex values here — these are the definitions, never var() refs.
const PARTNERS: Record<string, Partner> = {
  // Kissht — royal blue + yellow
  "9490f878-415d-4d19-91cd-b126f7b7d979": {
    name: "Kissht",
    logo: "https://marketing-content-images.s3.ap-south-1.amazonaws.com/kissht.jpg",
    vars: {
      "--brand-50": "#EEF1FA",
      "--brand-100": "#D9E0F2",
      "--brand-200": "#B3C1E4",
      "--brand-300": "#8CA2D6",
      "--brand-400": "#5C71B8",
      "--brand-500": "#3A53A5", // Kissht royal blue
      "--brand-600": "#324893",
      "--brand-700": "#26376E",
      "--brand-800": "#1F2D5C",
      "--brand-900": "#141D3B",
      "--brand-accent": "#FCD00F", // Kissht yellow
    },
    blue: {
      50: "#EEF1FA",
      100: "#D9E0F2",
      200: "#B3C1E4",
      300: "#8CA2D6",
      400: "#5C71B8",
      500: "#3A53A5",
      600: "#324893",
      700: "#2A3D7D",
      800: "#1F2D5C",
      900: "#141D3B",
    },
    // Branded CTAs = yellow bg + royal blue text
    cta: { bg: "#FCD00F", fg: "#3A53A5", hover: "#E6BC0D", active: "#D9AB0D" },
  },

  // NearMeDeals — electric blue + deep navy, sampled from the logo artwork.
  // Single-hue brand, so no `cta`: solid buttons ride the electric-blue scale.
  "5ac6060e-bc66-4bdb-83b5-5d1897f7f65e": {
    name: "NearMeDeals",
    logo: "https://marketing-content-images.s3.ap-south-1.amazonaws.com/LogoNew_White.png",
    vars: {
      "--brand-50": "#EFF5FF",
      "--brand-100": "#D8E8FF",
      "--brand-200": "#AFD0FE",
      "--brand-300": "#78B2FD",
      "--brand-400": "#0083FC", // logo: dominant bright blue
      "--brand-500": "#0B63F6", // NearMeDeals electric blue
      "--brand-600": "#0A4FD0",
      "--brand-700": "#073AA8",
      "--brand-800": "#01287C", // logo: pin-disc navy
      "--brand-900": "#04123F", // logo: tagline navy
      "--brand-accent": "#01B2FD", // logo highlight; pair with dark text
    },
    blue: {
      50: "#EFF5FF",
      100: "#D8E8FF",
      200: "#AFD0FE",
      300: "#78B2FD",
      400: "#0083FC",
      500: "#0B63F6",
      600: "#0A4FD0",
      700: "#073AA8",
      800: "#01287C",
      900: "#04123F",
    },
  },

  // Vishtra Money — royal sapphire blue + metallic gold, sampled from the logo artwork.
  "25d19710-d43e-4344-8c39-a74507002c09": {
    name: "Vishtra Money",
    logo: "/v_money_new.png",
    supportEmail: "info@vishtra.com",
    vars: {
      "--brand-50": "#EFF4FA",
      "--brand-100": "#D7E4F5",
      "--brand-200": "#B0C9EC",
      "--brand-300": "#80A9E0",
      "--brand-400": "#4D85D1",
      "--brand-500": "#1B56AA", // logo: royal sapphire blue
      "--brand-600": "#164588",
      "--brand-700": "#113569",
      "--brand-800": "#0C254A",
      "--brand-900": "#07172E",
      "--brand-accent": "#D4AF37", // logo: metallic gold swoosh & arrow
    },
    blue: {
      50: "#EFF4FA",
      100: "#D7E4F5",
      200: "#B0C9EC",
      300: "#80A9E0",
      400: "#4D85D1",
      500: "#1B56AA",
      600: "#164588",
      700: "#113569",
      800: "#0C254A",
      900: "#07172E",
    },
    // Branded CTAs = metallic gold bg + dark navy text
    cta: { bg: "#D4AF37", fg: "#07172E", hover: "#C29E2E", active: "#AF8D25" },
  },

  // Coupl — electric violet + neon magenta gradient, sampled from the circular badge logo artwork.
  "2638e67d-30b4-4643-98a8-5ce858054104": {
    name: "Coupl",
    logo: "/coupl_logo.png",
    supportEmail: "support@coupl.money",
    vars: {
      "--brand-50": "#F8F0FF",
      "--brand-100": "#EEDCFF",
      "--brand-200": "#DDB8FE",
      "--brand-300": "#C387FE",
      "--brand-400": "#A44EFE",
      "--brand-500": "#7E16FE", // logo: core electric violet
      "--brand-600": "#680DDB",
      "--brand-700": "#5409B5",
      "--brand-800": "#3E058F",
      "--brand-900": "#2A0266",
      "--brand-accent": "#CE03FE", // logo: neon magenta / fuchsia
    },
    blue: {
      50: "#F8F0FF",
      100: "#EEDCFF",
      200: "#DDB8FE",
      300: "#C387FE",
      400: "#A44EFE",
      500: "#7E16FE",
      600: "#680DDB",
      700: "#5409B5",
      800: "#3E058F",
      900: "#2A0266",
    },
    cta: { bg: "#7E16FE", fg: "#FFFFFF", hover: "#6B0EE0", active: "#5808BC" },
  },
};

const DEFAULT_VARS: Record<string, string> = {
  "--brand-50": "#EFF6FF",
  "--brand-100": "#DBEAFE",
  "--brand-200": "#BFDBFE",
  "--brand-300": "#93C5FD",
  "--brand-400": "#60A5FA",
  "--brand-500": "#3B82F6",
  "--brand-600": "#2563EB",
  "--brand-700": "#1D4ED8",
  "--brand-800": "#1E40AF",
  "--brand-900": "#1E3A8A",
  "--brand-accent": "#F59E0B",
};

const resolveAgentId = (): string => {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  const fromQuery = params.get("agentId") || params.get("agent_id");
  if (fromQuery) {
    try {
      localStorage.setItem("referredByAgent", fromQuery);
      localStorage.setItem("whitelabel_agent_id", fromQuery);
    } catch {}
    return fromQuery;
  }
  return (
    localStorage.getItem("referredByAgent") ??
    localStorage.getItem("whitelabel_agent_id") ??
    ""
  );
};

export const getBranding = (): Partner | null =>
  PARTNERS[resolveAgentId()] ?? null;

export const applyBrandingToDocument = () => {
  if (typeof window === "undefined") return;
  const brand = getBranding();
  if (brand) {
    document.title = brand.name;
    const existingIcons = document.querySelectorAll('link[rel*="icon"]');
    existingIcons.forEach((el) => {
      if (el.parentNode) el.parentNode.removeChild(el);
    });
    ['icon', 'shortcut icon', 'apple-touch-icon'].forEach((rel) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.type = 'image/png';
      link.href = brand.logo;
      document.head.appendChild(link);
    });
  } else {
    document.title = "ClickPe Portal";
  }
};

// ponytail: brand colors ride on CSS vars so every hardcoded hex flips at once.
export const applyBrandColors = () => {
  const vars = getBranding()?.vars ?? DEFAULT_VARS;
  const root = document.documentElement.style;
  Object.entries(vars).forEach(([k, v]) => root.setProperty(k, v));
  applyBrandingToDocument();
};

applyBrandColors();
applyBrandingToDocument();

